import {
  APP_INIT_SUCCESS,
  APP_ONLINE_STATUS,
  APP_SET_IS_ONBOARDING,
  APP_SEARCH_PAGE_SHORTLIST_TOGGLE,
  APP_SKIP_ONBOARDING,
  APP_SWITCH_OFF_DESIGN_BANNER,
  FOCUS_ON_EXPLORER,
  IMPORTANT_UPDATE_AVAILABLE,
  LIBRARY_SELECT,
  LIBRARY_USER_DEFAULT_LOAD,
  RESTART_SUBSCRIPTION,
  SCREEN_CHANGES,
  APP_PAGE_CHANGE_LOAD,
  SHORTLISTS_TOGGLE,
  SIDEBAR_CLOSE,
  SIDEBAR_OPEN,
  WINDOW_RESIZE,
  APP_REQUEST_STARTED,
  APP_REQUEST_COMPLETED,
  APP_REQUEST_RESET,
  APP_PLAYLIST_CREATOR_SETTINGS_UPDATED,
  APP_PLAYLIST_CREATOR_WIDTH_UPDATED,
} from '@disco/constants/ActionTypes'
import { PHONE, TABLET } from '@disco/constants/DeviceTypes'
import type { DeviceType } from '@disco/constants/DeviceTypes'
import { getSlug } from '@disco/utils/string-utils'

import { detect } from 'detect-browser'
import mobile from 'is-mobile'
import get from 'lodash/get'
import type { Group } from 'types/group'
const isPhoneSized = (maxPhoneWidth: number = 600): boolean => {
  if (typeof window === 'undefined') return false
  if (!window.screen) return false
  const size =
    window.screen.width < window.screen.height
      ? window.screen.width
      : window.screen.height
  return size <= maxPhoneWidth
}

function getDeviceType() {
  const isPhone = mobile()
  const isTablet =
    !isPhone &&
    mobile({
      tablet: true,
    })
  if (isPhone && isPhoneSized()) return PHONE
  if (isTablet) return TABLET
  return null
}

export const isPathMatch = (
  pathname: string | undefined,
  path: string,
): boolean => {
  if (!pathname) return false
  return pathname.includes(path)
}

const browser = detect()
const os = getSlug(get(browser, 'os', '') ?? '')
const isMobile = getDeviceType()
const pathname =
  typeof window !== 'undefined' && window.location
    ? window.location.pathname
    : ''
const isSearchPage = isPathMatch(pathname, '/search')

export const initialState: State = {
  sidebarOpen: !isSearchPage,
  shortlistsOpen: isMobile ? false : true,
  shortlistUserSettings: {
    location: undefined,
    settings: {
      explorer: {
        visible: isMobile ? 'Hidden' : 'Visible',
        actionType: 'Load Page',
      },
      search: { visible: 'Hidden', actionType: 'Load Page' },
    },
  },
  currentLocation: isSearchPage ? 'Search' : 'Explorer',
  windowWidth: typeof window !== 'undefined' ? window.innerWidth : 0,
  importantUpdateAvailable: false,
  selectedLibrary: null,
  launchedOnMobileDevice:
    isPhoneSized() &&
    mobile({
      tablet: true,
    }),
  mobileDeviceType: getDeviceType(),
  os: os ? os.toLowerCase() : null,
  online: true,
  initialized: false,
  hideSwitchDesignBanner: false,
  restartSubscription: false,
  screen: {
    sm: false,
    md: false,
    lg: false,
  },
  isOnboarding: false,
  isSearchPageShortlistOpen: false,
  isOnboardingSkipped: false,
  requestsTotal: 0,
  requestsCompleted: 0,
  playlistCreatorWidth: 400,
  /*
  launchedOnMobileDevice: true,
  mobileDeviceType: PHONE,
  os: 'ios'
  */
}

type toggleStateProps = {
  actionType:
    | 'Load Page'
    | 'Click Button'
    | 'Drag Track'
    | 'Duplicate Playlist'
    | 'Edit Playlist'
    | 'Add Track to Playlist'
  visible: 'Visible' | 'Hidden'
}

export type PlaylistCreatorUserSettings = {
  location: 'Explorer' | 'Search' | undefined
  settings: {
    explorer?: toggleStateProps
    search?: toggleStateProps
  }
}

export type State = {
  sidebarOpen: boolean
  shortlistsOpen: boolean
  shortlistUserSettings: PlaylistCreatorUserSettings
  windowWidth: number
  importantUpdateAvailable: boolean
  selectedLibrary: Group | null | undefined
  launchedOnMobileDevice: boolean
  mobileDeviceType: null | DeviceType
  os: string | null | undefined
  online: boolean
  initialized: boolean
  restartSubscription: boolean
  currentLocation: string
  screen: {
    sm: boolean
    md: boolean
    lg: boolean
  }
  isOnboarding: boolean
  isSearchPageShortlistOpen: boolean
  isOnboardingSkipped: boolean
  hideSwitchDesignBanner: boolean
  requestsTotal: number
  requestsCompleted: number
  playlistCreatorWidth: number
}
export function appReducer(
  state: State = initialState,
  action: any = {},
): State {
  const { type } = action

  switch (type) {
    case WINDOW_RESIZE:
      if (state.windowWidth === action.windowWidth) return state
      return { ...state, windowWidth: action.windowWidth }

    case SIDEBAR_OPEN:
      return {
        ...state,
        sidebarOpen: true,
      }

    case SIDEBAR_CLOSE:
      return {
        ...state,
        sidebarOpen: false,
      }

    case LIBRARY_USER_DEFAULT_LOAD:
      return {
        ...state,
        selectedLibrary: action.library,
        sidebarOpen: !isSearchPage,
        shortlistsOpen: state.shortlistsOpen,
        isSearchPageShortlistOpen: state.isSearchPageShortlistOpen,
        shortlistUserSettings: {
          location: isSearchPage ? 'Search' : 'Explorer',
          settings: state.shortlistUserSettings.settings,
        },
      }

    case LIBRARY_SELECT:
      return { ...state, selectedLibrary: action.library }

    case IMPORTANT_UPDATE_AVAILABLE:
      return { ...state, importantUpdateAvailable: true }

    case FOCUS_ON_EXPLORER:
      return {
        ...state,
        shortlistsOpen: state.shortlistsOpen,
        isSearchPageShortlistOpen: state.isSearchPageShortlistOpen,
      }

    case SHORTLISTS_TOGGLE: {
      return {
        ...state,
        shortlistsOpen: !state.shortlistsOpen,
      }
    }

    case APP_ONLINE_STATUS:
      return { ...state, online: action.online }

    case APP_INIT_SUCCESS:
      return {
        ...state,
        initialized: true,
      }

    case APP_SWITCH_OFF_DESIGN_BANNER:
      return { ...state, hideSwitchDesignBanner: true }

    case RESTART_SUBSCRIPTION:
      return { ...state, restartSubscription: true }

    case SCREEN_CHANGES:
      return {
        ...state,
        screen: action.screen,
        shortlistUserSettings: state.shortlistUserSettings,
      }

    case APP_SET_IS_ONBOARDING:
      return {
        ...state,
        isOnboarding: action.isOnboarding,
        sidebarOpen: action.isOnboarding ? false : state.sidebarOpen,
      }

    // Update App settings on page/path change when necessary
    case APP_PAGE_CHANGE_LOAD: {
      // filter out settings that are being updated to avoid duplication
      const updateSettingsKeys = new Set(Object.keys(action.updateSettings))
      const filteredState = Object.entries(state)
        .filter(([key, _value]) => !updateSettingsKeys.has(key))
        .reduce((obj, [key, value]) => {
          obj[key] = value
          return obj
        }, {} as Record<string, any>)

      return {
        ...filteredState,
        ...action.updateSettings,
      }
    }

    case APP_SEARCH_PAGE_SHORTLIST_TOGGLE: {
      return {
        ...state,
        isSearchPageShortlistOpen: !state.isSearchPageShortlistOpen,
      }
    }

    case APP_PLAYLIST_CREATOR_WIDTH_UPDATED: {
      return {
        ...state,
        playlistCreatorWidth: action.playlistCreatorWidth,
      }
    }
    case APP_PLAYLIST_CREATOR_SETTINGS_UPDATED: {
      const { shortlistUserSettings } = action
      const updatedLocation =
        shortlistUserSettings?.location || isSearchPage ? 'Search' : 'Explorer'
      const updatedSettings =
        shortlistUserSettings.settings ||
        state.shortlistUserSettings.settings[updatedLocation.toLowerCase()]
      return {
        ...state,
        shortlistUserSettings: {
          location: updatedLocation,
          settings: {
            ...state.shortlistUserSettings.settings,
            ...updatedSettings,
          },
        },
      }
    }

    case APP_SKIP_ONBOARDING:
      return {
        ...state,
        isOnboardingSkipped: true,
        isOnboarding: false,
        sidebarOpen: true,
      }
    case APP_REQUEST_RESET:
      return { ...state, requestsTotal: 0, requestsCompleted: 0 }
    case APP_REQUEST_STARTED:
      return { ...state, requestsTotal: state.requestsTotal + 1 }
    case APP_REQUEST_COMPLETED:
      return {
        ...state,
        requestsCompleted: state.requestsCompleted + 1,
      }
    default:
      return state
  }
}
